import React from 'react';
import { Link } from 'react-router-dom';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import { pricingRules, printAreas } from '@/data/products';
import { getCapPresentation, getCapPrintExample } from '@/data/capPresentation';
import { headwearContent } from '@/data/headwearContent';
import { buildPrintedCapsQuantityOffer, PRINTED_CAPS_OFFER_VERSION, printedCapsQuoteCopy } from '@/lib/printedCapsQuantityOffer';
import CapEditorialImage from './CapEditorialImage';
import './capEditorial.css';

const formatILS = (value) => new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(value);

function fromPrice(product) {
  return (pricingRules[product.sku]?.tiers || [])
    .filter((tier) => Number.isFinite(Number(tier.price)))
    .reduce((lowest, tier) => !lowest || Number(tier.price) < Number(lowest.price) ? tier : lowest, null);
}

export default function PrintedCapsLanding({ landing, surfacedProducts }) {
  const content = headwearContent[landing.key];
  const lead = surfacedProducts.find((product) => product.sku === 'KR2020');
  const offer = buildPrintedCapsQuantityOffer(lead);
  const leadImage = getCapPrintExample(lead);
  const supportingExamples = surfacedProducts
    .filter((product) => product.sku !== lead?.sku)
    .map((product) => ({ product, example: getCapPrintExample(product) }))
    .filter(({ example }) => example);
  const front = printAreas.cap_front;

  return <div className="caps-editorial" dir="rtl">
    <section className="caps-hero">
      <div className="caps-hero__inner">
        <div className="caps-hero__copy">
          <p className="caps-eyebrow">PRINTEAM / כובעים עם הדפס לצוות</p>
          <h1>{content.title}</h1>
          <p className="caps-hero__intro">{content.description}</p>
          {offer && <section className="caps-quantity-offer" aria-labelledby="caps-quantity-title" data-caps-offer-version={PRINTED_CAPS_OFFER_VERSION} data-product-sku={offer.configuration.sku}>
            <h2 id="caps-quantity-title">{printedCapsQuoteCopy.heading}</h2>
            <p className="caps-quantity-offer__configuration">{printedCapsQuoteCopy.configuration}</p>
            <div className="caps-quantity-grid">
              {offer.examples.map((example) => <article key={example.quantity} className="caps-quantity-card">
                <h3><bdi>{example.quantity}</bdi> כובעים</h3>
                <p className="caps-quantity-card__total"><bdi>{formatILS(example.totalWithoutDeliveryInclVat)}</bdi></p>
                <p className="caps-quantity-card__scope">לכמות, ללא משלוח</p>
                <p className="caps-quantity-card__unit"><bdi>{formatILS(example.unitTotalInclVat)}</bdi> ליחידה עם ההדפס</p>
                <p className="caps-quantity-card__delivery">עם משלוח: <bdi>{formatILS(example.totalWithDeliveryInclVat)}</bdi><br /><span>תוספת <bdi>{formatILS(example.deliveryInclVat)}</bdi></span></p>
              </article>)}
            </div>
            <p className="caps-quantity-offer__included">{printedCapsQuoteCopy.included}</p>
            <p className="caps-quantity-offer__note">{printedCapsQuoteCopy.discounts}</p>
            <p className="caps-quantity-offer__note">{printedCapsQuoteCopy.availability}</p>
          </section>}
          <div className="caps-actions">
            <Link className="caps-button" to={landing.primaryCta.to}>{landing.primaryCta.label} <ArrowLeft size={19} aria-hidden="true" /></Link>
            <Link className="caps-text-link" to={landing.secondaryCta.to}>{landing.secondaryCta.label}</Link>
          </div>
        </div>
        {leadImage && <figure className="caps-hero__visual">
          <CapEditorialImage name={leadImage.image} alt={leadImage.altHe} priority sizes="(min-width: 1440px) 742px, (min-width: 640px) 52vw, calc(100vw - 32px)" />
          <figcaption><span>דוגמת עיצוב להמחשה</span><Link to={`/product/${lead.sku}`}>{lead.nameHe} · <bdi>{lead.sku}</bdi> <ArrowLeft size={16} aria-hidden="true" /></Link></figcaption>
        </figure>}
      </div>
    </section>

    <section className="caps-models caps-section" aria-labelledby="caps-models-title">
      <div className="caps-section-heading">
        <div><p className="caps-eyebrow">אפשר גם לבחור דגם אחר</p><h2 id="caps-models-title">ארבעה דגמים, מחיר ואפשרויות לכל דגם</h2></div>
        <p>{content.selectionIntro}</p>
      </div>
      <div className="caps-model-grid">
        {surfacedProducts.map((product) => {
          const presentation = getCapPresentation(product);
          if (!presentation) return null;
          const cheapestTier = fromPrice(product);
          return <article key={product.sku} className="caps-model">
            <Link to={`/product/${product.sku}`} className="caps-model__link" aria-label={`לצפייה במוצר ${product.nameHe || product.name}`}>
              <div className="caps-model__photo"><CapEditorialImage name={presentation.front} alt={presentation.altFrontHe} height="1800" sizes="(min-width: 1000px) 25vw, 50vw" /></div>
              <div className="caps-model__body">
                <p className="caps-model__sample">ללא מיתוג · {presentation.color}</p>
                <div className="caps-model__identity"><h3>{product.nameHe || product.name}</h3><bdi>{product.sku}</bdi></div>
                <p className="caps-model__options">{product.colors.length} צבעים לבחירה</p>
                <div className="caps-model__action"><span>{cheapestTier == null ? 'לצפייה במחיר המוצר' : `מחיר המוצר מ־${formatILS(cheapestTier.price)} ליחידה (${cheapestTier.min}+ יח׳), כולל מע״מ`}</span><ArrowLeft size={18} aria-hidden="true" /></div>
              </div>
            </Link>
          </article>;
        })}
      </div>
      <Link to={landing.catalogPath} className="caps-text-link caps-more">לכל דגמי כובעי המצחייה <ArrowLeft size={17} aria-hidden="true" /></Link>
    </section>

    <section className="caps-story" aria-labelledby="caps-story-title">
      <div className="caps-section caps-story__grid">
        <div className="caps-story__copy"><p className="caps-eyebrow">מקום לעיצוב שלכם</p><h2 id="caps-story-title">פריט קטן.<br />מקום לרעיון שלכם.</h2><p>איור צבעוני, סמל נקי או גרפיקה לצוות — מתחילים בדגם ובשטח המיתוג שמתאימים לעיצוב.</p><p className="caps-print-area">שטח חזית מרבי: <bdi>{front.maxWCm} × {front.maxHCm}</bdi> ס״מ</p><Link className="caps-text-link" to="/methods-of-branding">לשיטות המיתוג <ArrowLeft size={17} aria-hidden="true" /></Link></div>
        {supportingExamples.map(({ product, example }) => <figure key={product.sku}><CapEditorialImage name={example.image} alt={example.altHe} sizes="(min-width: 1440px) 443px, (min-width: 1000px) 30vw, 45vw" /><figcaption>דוגמת עיצוב להמחשה · <bdi>{product.sku}</bdi> · הדמיה, אינה עבודת לקוח</figcaption></figure>)}
      </div>
    </section>

    <section className="caps-section caps-process" id="how" aria-labelledby="caps-process-title">
      <div className="caps-section-heading"><h2 id="caps-process-title">מהרעיון לכובע שלכם</h2><Link className="caps-text-link" to={landing.catalogPath}>מתחילים בבחירת דגם <ArrowLeft size={17} aria-hidden="true" /></Link></div>
      <ol>{landing.workflow.map((step, index) => <li key={step.title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{step.title}</h3><p>{step.body}</p></li>)}</ol>
    </section>
    <section className="caps-final">
      <div className="caps-section caps-final__inner"><h2>הכובע הבא מתחיל בעיצוב שלכם</h2><div className="caps-actions"><Link className="caps-button" to={landing.finalCta.to}>{landing.finalCta.label}<ArrowLeft size={18} aria-hidden="true" /></Link><Link className="caps-text-link" to={landing.finalSecondaryCta.to}>{landing.finalSecondaryCta.label}</Link></div></div>
    </section>
  </div>;
}
